import { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Package, Mail } from "lucide-react";
import SiteHeader from "@/components/shop/SiteHeader";
import SiteFooter from "@/components/shop/SiteFooter";
import CartDrawer from "@/components/shop/CartDrawer";
import { useCart } from "@/context/CartContext";
import { api, fmtUSD } from "@/lib/api";

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { clear } = useCart();
  const [status, setStatus] = useState({ payment_status: "pending" });
  const [expired, setExpired] = useState(false);
  const clearedRef = useRef(false);
  const attemptRef = useRef(0);

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (cancelled) return;
        setStatus(data);
        if (data.payment_status === "paid") {
          if (!clearedRef.current) {
            clear();
            clearedRef.current = true;
          }
          return;
        }
        attemptRef.current += 1;
        if (attemptRef.current > 8) {
          setExpired(true);
          return;
        }
        setTimeout(poll, 2000);
      } catch (e) {
        if (cancelled) return;
        console.error(e);
        attemptRef.current += 1;
        if (attemptRef.current > 8) {
          setExpired(true);
          return;
        }
        setTimeout(poll, 2000);
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, clear]);

  const paid = status.payment_status === "paid";

  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid="checkout-success-page">
      <SiteHeader />
      <CartDrawer />

      <section className="mx-auto max-w-2xl px-5 sm:px-8 py-16 md:py-24 text-center">
        {paid ? (
          <>
            <motion.div
              initial={{ scale: 0, rotate: -30 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ type: "spring", damping: 12 }}
              className="mx-auto w-20 h-20 rounded-full bg-emerald-50 flex items-center justify-center"
              data-testid="success-icon"
            >
              <CheckCircle2 size={44} className="text-emerald-500" />
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="mt-6 font-display text-4xl md:text-5xl font-black text-[#0f2a5c]"
              data-testid="success-title"
            >
              Thank you!
            </motion.h1>
            <p className="mt-3 text-lg text-slate-600">
              Your Morning Soft order is confirmed.
            </p>

            <div className="mt-10 text-left rounded-2xl border border-slate-100 p-6" data-testid="order-items-summary">
              <p className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-4">Your order</p>
              {(status.items || []).map((it) => (
                <div key={it.slug} className="flex gap-3 py-3 border-b border-slate-50 last:border-0">
                  <img src={it.image} alt={it.name} className="w-14 h-14 rounded-xl bg-sky-50 p-1 object-contain" />
                  <div className="flex-1 flex flex-col justify-center">
                    <p className="text-sm font-semibold">{it.name}</p>
                    <p className="text-xs text-slate-500">Qty {it.quantity}</p>
                  </div>
                  <p className="text-sm font-bold">{fmtUSD(it.price * it.quantity)}</p>
                </div>
              ))}
              <div className="mt-4 pt-4 border-t border-slate-100 flex justify-between font-display font-extrabold text-[#173a7a]">
                <span>Total</span>
                <span data-testid="success-total">{fmtUSD(status.total || 0)}</span>
              </div>
              {status.customer_email && (
                <p className="mt-4 text-sm text-slate-500 flex items-center gap-2">
                  <Mail size={14} /> A receipt will arrive at <b>{status.customer_email}</b>.
                </p>
              )}
              <p className="mt-2 text-xs text-slate-400 flex items-center gap-2">
                <Package size={12} /> Session: {sessionId?.slice(0, 24)}…
              </p>
            </div>

            <Link
              to="/shop"
              className="inline-block mt-10 px-8 py-3.5 rounded-full bg-[#173a7a] text-white font-semibold hover:bg-[#1a4694]"
              data-testid="success-continue-shopping"
            >
              Continue Shopping
            </Link>
          </>
        ) : expired ? (
          <div data-testid="success-expired">
            <p className="font-display text-3xl font-bold text-slate-900">We&apos;re still confirming your payment.</p>
            <p className="mt-3 text-slate-600">You&apos;ll receive a confirmation email once it clears. If nothing arrives shortly, contact us.</p>
            <Link to="/contact" className="inline-block mt-6 px-6 py-3 rounded-full bg-[#173a7a] text-white font-semibold">
              Contact Support
            </Link>
          </div>
        ) : (
          <div data-testid="success-pending">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1.4, repeat: Infinity, ease: "linear" }}
              className="mx-auto w-12 h-12 border-4 border-sky-100 border-t-[#173a7a] rounded-full"
            />
            <p className="mt-6 font-display text-2xl font-bold text-slate-900">Confirming your payment…</p>
            <p className="mt-2 text-slate-500 text-sm">This usually takes just a few seconds.</p>
          </div>
        )}
      </section>

      <SiteFooter />
    </div>
  );
}
